import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { g as getNamespacedTagFor } from './index-739b155e.js';
import { a as asyncRequestAnimationFrame } from './raf-4d880dcc.js';
import { i as isArray, M as MapCache, S as Stack, b as baseIsEqual, k as keys, a as isLength, c as isIndex, d as isArguments, e as Set$1, s as setToArray, f as SetCache, g as cacheHas } from './_baseIsEqual-00d982ac.js';
import { i as isSymbol, S as Symbol, a as isObject } from './isObject-c1cee6a1.js';

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol ? Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap(value, baseToString) + '';
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$2) ? '-0' : result;
}

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

/**
 * This method returns `undefined`.
 *
 * @static
 * @memberOf _
 * @since 2.3.0
 * @category Util
 * @example
 *
 * _.times(2, _.noop);
 * // => [undefined, undefined]
 */
function noop() {
  // No operation performed.
}

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseFindIndex(array, predicate, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 1 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.isNaN` without support for number objects.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
 */
function baseIsNaN(value) {
  return value !== value;
}

/**
 * A specialized version of `_.indexOf` which performs strict equality
 * comparisons of values, i.e. `===`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function strictIndexOf(array, value, fromIndex) {
  var index = fromIndex - 1,
      length = array.length;

  while (++index < length) {
    if (array[index] === value) {
      return index;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseIndexOf(array, value, fromIndex) {
  return value === value
    ? strictIndexOf(array, value, fromIndex)
    : baseFindIndex(array, baseIsNaN, fromIndex);
}

/**
 * A specialized version of `_.includes` for arrays without support for
 * specifying an index to search from.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludes(array, value) {
  var length = array == null ? 0 : array.length;
  return !!length && baseIndexOf(array, value, 0) > -1;
}

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = MapCache;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray(value)) {
    return value;
  }
  return isKey(value, object) ? [value] : stringToPath(toString(value));
}

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new Stack;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$1 | COMPARE_UNORDERED_FLAG$1, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject(value);
}

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = keys(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, isStrictComparable(value)];
  }
  return result;
}

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function matchesStrictComparable(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches(source) {
  var matchData = getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return matchesStrictComparable(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || baseIsMatch(object, source, matchData);
  };
}

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHasIn(object, key) {
  return object != null && key in Object(object);
}

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath(object, path, hasFunc) {
  path = castPath(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = toKey(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength(length) && isIndex(key, length) &&
    (isArray(object) || isArguments(object));
}

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn(object, path) {
  return object != null && hasPath(object, path, baseHasIn);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty(path, srcValue) {
  if (isKey(path) && isStrictComparable(srcValue)) {
    return matchesStrictComparable(toKey(path), srcValue);
  }
  return function(object) {
    var objValue = get(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn(object, path)
      : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
  };
}

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep(path) {
  return function(object) {
    return baseGet(object, path);
  };
}

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property(path) {
  return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
}

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity;
  }
  if (typeof value == 'object') {
    return isArray(value)
      ? baseMatchesProperty(value[0], value[1])
      : baseMatches(value);
  }
  return property(value);
}

/**
 * This function is like `arrayIncludes` except that it accepts a comparator.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @param {Function} comparator The comparator invoked per element.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludesWith(array, value, comparator) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (comparator(value, array[index])) {
      return true;
    }
  }
  return false;
}

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/**
 * Creates a set object of `values`.
 *
 * @private
 * @param {Array} values The values to add to the set.
 * @returns {Object} Returns the new set.
 */
var createSet = !(Set$1 && (1 / setToArray(new Set$1([,-0]))[1]) == INFINITY) ? noop : function(values) {
  return new Set$1(values);
};

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * The base implementation of `_.uniqBy` without support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee] The iteratee invoked per element.
 * @param {Function} [comparator] The comparator invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 */
function baseUniq(array, iteratee, comparator) {
  var index = -1,
      includes = arrayIncludes,
      length = array.length,
      isCommon = true,
      result = [],
      seen = result;

  if (comparator) {
    isCommon = false;
    includes = arrayIncludesWith;
  }
  else if (length >= LARGE_ARRAY_SIZE) {
    var set = iteratee ? null : createSet(array);
    if (set) {
      return setToArray(set);
    }
    isCommon = false;
    includes = cacheHas;
    seen = new SetCache;
  }
  else {
    seen = iteratee ? [] : result;
  }
  outer:
  while (++index < length) {
    var value = array[index],
        computed = iteratee ? iteratee(value) : value;

    value = (comparator || value !== 0) ? value : 0;
    if (isCommon && computed === computed) {
      var seenIndex = seen.length;
      while (seenIndex--) {
        if (seen[seenIndex] === computed) {
          continue outer;
        }
      }
      if (iteratee) {
        seen.push(computed);
      }
      result.push(value);
    }
    else if (!includes(seen, computed, comparator)) {
      if (seen !== result) {
        seen.push(computed);
      }
      result.push(value);
    }
  }
  return result;
}

/**
 * This method is like `_.uniq` except that it accepts `iteratee` which is
 * invoked for each element in `array` to generate the criterion by which
 * uniqueness is computed. The order of result values is determined by the
 * order they occur in the array. The iteratee is invoked with one argument:
 * (value).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 * @example
 *
 * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
 * // => [2.1, 1.2]
 *
 * // The `_.property` iteratee shorthand.
 * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
 * // => [{ 'x': 1 }, { 'x': 2 }]
 */
function uniqBy(array, iteratee) {
  return (array && array.length) ? baseUniq(array, baseIteratee(iteratee)) : [];
}

/**
 * Check if the value is considered empty
 * @param {unknown} value
 */
function isValueEmpty(value) {
  return value === '' || value === null || value === undefined;
}
/**
 * Check if the slotted control in <market-row slot="control-row> is a valid control row input
 * @param {unknown} control
 */
function isValidControl(control) {
  var _a;
  if (!control) {
    return false;
  }
  const tagName = (_a = control.tagName) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase();
  const validControlTags = [
    getNamespacedTagFor('market-checkbox').toLocaleLowerCase(),
    getNamespacedTagFor('market-radio').toLocaleLowerCase(),
    getNamespacedTagFor('market-toggle').toLocaleLowerCase(),
  ];
  return validControlTags.includes(tagName);
}

const marketListCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}::slotted(.market-action-card){margin-bottom:var(--action-card-spacing)}::slotted(.market-input-search){margin-bottom:var(--core-metrics-spacing-100)}.hidden,::slotted(.hidden){display:none}::slotted(.market-row.hide-bottom-border)::before{display:none}.market-empty-state,::slotted(.market-empty-state){padding-right:0;padding-left:0;border:0}::slotted(.market-drag-cursor){--drag-cursor-height:4px;display:block;height:var(--drag-cursor-height);margin:calc(var(--drag-cursor-height) / 2 * -1) 0;background:var(--core-emphasis-fill-color)}";

const MarketList = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketListSelectionsDidChange = createEvent(this, "marketListSelectionsDidChange", 7);
    this.marketListSlotChange = createEvent(this, "marketListSlotChange", 7);
    this.marketListItemsReordered = createEvent(this, "marketListItemsReordered", 7);
    this.marketListItemsFiltered = createEvent(this, "marketListItemsFiltered", 7);
    this.value = '';
    this.interactive = false;
    this.transient = false;
    this.multiselect = false;
    this.name = undefined;
    this.filterStrategy = 'textcontent';
    this.reorderable = 'off';
    this.hasSearch = false;
    this.items = undefined;
    this.selections = new Set();
    this.filteredItems = undefined;
  }
  reorderableWatcher() {
    this.setReorderable();
  }
  valueWatcher() {
    this.setSelectionsFromValue();
  }
  /* Listen for the marketRowSelected event which is emitted by slotted market-row elements
  when they are clicked */
  rowSelectedEventHandler(e) {
    this.handleItemSelectedEvent(e.target);
  }
  /* Listen for the marketRowDeselected event which is emitted by slotted market-row elements
  when they are clicked */
  rowDeselectedEventHandler(e) {
    this.handleItemDeselectedEvent(e.target);
  }
  /* Listen for the marketCardSelected event which is emitted by slotted market-action-card elements
  when they are clicked */
  cardSelectedEventHandler(e) {
    this.handleItemSelectedEvent(e.target);
  }
  /* Listen for the marketCardDeselected event which is emitted by slotted market-action-card elements
  when they are clicked */
  cardDeselectedEventHandler(e) {
    this.handleItemDeselectedEvent(e.target);
  }
  /**
   * Listen for `marketInputSearchValueChange` which is emitted by the slotted `market-input-search`
   */
  marketInputSearchValueChangeEventHander({ detail }) {
    this.filterItems(detail.value);
  }
  /**
   * Fired on a target element when an item is dragged over the target.
   */
  marketDragEnterHandler(e) {
    const draggedEl = e.detail.el;
    const dragTarget = e.target;
    if (!this.isValidDrag(draggedEl, dragTarget))
      return;
    this.initDragCursor();
    // if the drag target is this list, append the cursor and return
    if (dragTarget === this.el) {
      this.el.append(this.dragCursor);
      return;
    }
    // otherwise, the drag target needs to be a non-control market-row
    const targetRow = dragTarget.closest(getNamespacedTagFor('market-row'));
    if (!targetRow || targetRow === this.controlRow)
      return;
    // determine whether to append or prepend cursor based on mouse position
    const { top, bottom } = targetRow.getBoundingClientRect();
    const halfway = (top + bottom) / 2;
    if (e.detail.y < halfway) {
      targetRow.before(this.dragCursor);
    }
    else {
      targetRow.after(this.dragCursor);
    }
  }
  /**
   * Fired on a target element when a dragged item leaves the target
   */
  marketDragLeaveHandler() {
    var _a;
    (_a = this.dragCursor) === null || _a === void 0 ? void 0 : _a.remove();
  }
  /**
   * Fired on a dragged item when it is released.
   * Useful to determine if a row was dragged out of the list.
   */
  marketDragEndHandler(e) {
    const draggedEl = e.target;
    const { dragTarget } = e.detail;
    if (!this.isValidDrag(draggedEl, dragTarget))
      return;
    // do nothing if the drag target is still inside this list
    if (dragTarget.closest(getNamespacedTagFor('market-list')) === this.el)
      return;
    // note: dragging externally means new index = -1
    const oldIndex = this.items.indexOf(draggedEl);
    const newIndex = -1;
    // emit the event and check for prevent default
    const { defaultPrevented } = this.marketListItemsReordered.emit({
      item: draggedEl,
      oldIndex,
      newIndex,
    });
    // if sort was prevented, prevent the drop event
    if (defaultPrevented) {
      e.preventDefault();
    }
  }
  /**
   * Fired on a target element when a dragged item is released over the target.
   */
  marketDragDropHandler(e) {
    var _a, _b;
    const draggedEl = e.detail.el;
    const dragTarget = e.target;
    if (!this.isValidDrag(draggedEl, dragTarget))
      return;
    // note: if item is external, then old index is -1
    const oldIndex = this.items.indexOf(draggedEl);
    // find new index
    const itemsWithoutEl = [...this.items];
    if (oldIndex >= 0) {
      itemsWithoutEl.splice(oldIndex, 1);
    }
    const newIndex = itemsWithoutEl.indexOf(this.dragCursor.previousElementSibling) + 1;
    // same index means no sort event
    if (newIndex === oldIndex) {
      (_a = this.dragCursor) === null || _a === void 0 ? void 0 : _a.remove();
      return;
    }
    // emit the event and check for prevent default
    const { defaultPrevented } = this.marketListItemsReordered.emit({
      item: draggedEl,
      oldIndex,
      newIndex,
    });
    // if default prevented, remove the drag cursor
    if (defaultPrevented) {
      (_b = this.dragCursor) === null || _b === void 0 ? void 0 : _b.remove();
      return;
    }
    // otherwise, insert the item
    this.dragCursor.replaceWith(draggedEl);
  }
  isValidDrag(el, target) {
    const sourceList = el.closest(getNamespacedTagFor('market-list'));
    const targetList = target.closest(getNamespacedTagFor('market-list'));
    // is the dragged element a market-row?
    if (el.tagName !== getNamespacedTagFor('market-row').toUpperCase())
      return false;
    // are there target and source lists?
    if (!targetList || !sourceList)
      return false;
    // is this list either the source or target list?
    if (this.el !== sourceList && this.el !== targetList)
      return false;
    // are the lists reorderable?
    if (sourceList.reorderable === 'off' || targetList.reorderable === 'off')
      return false;
    // if target & source lists are different, are they both externally reorderable?
    if (sourceList !== targetList && (sourceList.reorderable !== 'external' || targetList.reorderable !== 'external'))
      return false;
    // looks like we're good!
    return true;
  }
  initDragCursor() {
    if (this.dragCursor)
      return;
    this.dragCursor = document.createElement('div');
    this.dragCursor.classList.add('market-drag-cursor');
  }
  getEventSelectionDetails() {
    const prevSelectionValues = (() => {
      if (typeof this.value === 'string') {
        return this.value ? this.value.split(',').filter((value) => !isValueEmpty(value)) : [];
      }
      else if (Array.isArray(this.value)) {
        return this.value.filter((value) => !isValueEmpty(value));
      }
      return this.value;
    })();
    const currentSelections = uniqBy([...this.selections].filter((item) => item !== this.controlRow), (item) => item.value);
    const currentSelectionValues = currentSelections.reduce((items, item) => {
      if (!isValueEmpty(item.value)) {
        items.push(item.value);
      }
      return items;
    }, []);
    return { currentSelections, currentSelectionValues, prevSelectionValues };
  }
  handleItemSelectedEvent(selectedItem) {
    if (selectedItem === this.controlRow) {
      this.selectAllItems();
    }
    else {
      this.selectItem(selectedItem);
    }
    const { currentSelections, currentSelectionValues, prevSelectionValues } = this.getEventSelectionDetails();
    this.value = currentSelectionValues.join(','); // reflect to DOM
    this.marketListSelectionsDidChange.emit({
      newSelection: selectedItem,
      newSelectionValue: selectedItem.value,
      newDeselection: null,
      newDeselectionValue: null,
      currentSelections,
      currentSelectionValues,
      prevSelectionValues,
    });
  }
  handleItemDeselectedEvent(deselectedItem) {
    // We check to see if the element is in our selections, since we may have
    // already manually deselected it due to another element being clicked.
    // We only want the code in the block to fire when `marketRowDeselected` is being
    // emitted due to a merchant actually clicking to deselect an element.
    if (!this.selections.has(deselectedItem)) {
      return;
    }
    if (deselectedItem === this.controlRow) {
      this.deselectAllItems();
    }
    else {
      this.deselectItem(deselectedItem);
    }
    const { currentSelections, currentSelectionValues, prevSelectionValues } = this.getEventSelectionDetails();
    this.value = currentSelectionValues.join(','); // reflect to DOM
    this.marketListSelectionsDidChange.emit({
      newSelection: null,
      newSelectionValue: null,
      newDeselection: deselectedItem,
      newDeselectionValue: deselectedItem.value,
      currentSelections,
      currentSelectionValues,
      prevSelectionValues,
    });
  }
  /**
   * Selects a given option from the list. Also handles deselecting
   * all other elements when not in multiselect mode.
   */
  selectItem(selectedItem) {
    /* Only if this list is interactive and *doesn't* allow multiple selections,
    deselect all the options except the one that was just selected */
    if (this.interactive) {
      if (!this.multiselect) {
        this.deselectItems([selectedItem]);
        this.selections = new Set([selectedItem]);
      }
      else {
        this.selections.add(selectedItem);
      }
    }
  }
  /**
   * Selects all multiselect list options.
   */
  selectAllItems() {
    var _a;
    if (!this.items || !this.interactive || !this.multiselect) {
      return;
    }
    // if items are being filtered, "Select all" only applies to visible items
    const items = ((_a = this.filteredItems) === null || _a === void 0 ? void 0 : _a.visible) || this.items;
    this.selections = new Set(items);
  }
  /**
   * Deselects a given option from the list.
   */
  deselectItem(deselectedItem) {
    this.selections.delete(deselectedItem);
  }
  /**
   * Deselects all other items other than the ones that were just selected.
   */
  deselectItems(selectedItems) {
    if (!this.items) {
      return;
    }
    this.items.forEach((item) => {
      /* Check to make sure the item isn't in the list of
        selected items (likely only the one that was just
        selected and triggered the callback) */
      if (!selectedItems.includes(item) && item.selected) {
        item.deselect();
      }
    });
  }
  /**
   * Deselects all list options.
   */
  deselectAllItems() {
    if (!this.selections || !this.interactive) {
      return;
    }
    // if items are being filtered, only deselect all visible filters
    if (this.filteredItems.visible) {
      this.filteredItems.visible.forEach((item) => {
        this.selections.delete(item);
      });
    }
    else {
      this.selections.clear();
    }
  }
  /**
   * If passed or updates interactive, ensure list itself is set to interactive mode.
   */
  syncListInteractiveWithItems() {
    if (this.items.length > 0 &&
      (this.items[0].tagName === getNamespacedTagFor('market-action-card').toUpperCase() ||
        (this.items[0].tagName === getNamespacedTagFor('market-row').toUpperCase() &&
          this.items[0].interactive === true)) &&
      !this.interactive) {
      // force list to be interactive if items are interactive rows
      // (i.e. when they contain slotted controls), or
      // items are action cards (which are always interactive)
      this.interactive = true;
    }
  }
  /**
   * Processes interactive, transient, and multiselect props and propagates these props
   * to children components whenever these props are updated.
   */
  processItems() {
    this.items.forEach((item) => {
      if (item.tagName === getNamespacedTagFor('market-action-card').toUpperCase()) {
        item.transient = this.transient;
        const cardRow = item.querySelector(getNamespacedTagFor('market-row'));
        if (cardRow) {
          this.setRowProperties(cardRow);
        }
      }
      else {
        this.setRowProperties(item);
      }
    });
  }
  getCurrentSelectionValues() {
    if (Array.isArray(this.value)) {
      return new Set(this.value);
    }
    return new Set(this.multiselect ? this.value.split(',') : [this.value]);
  }
  setRowProperties(row) {
    row.interactive = this.interactive;
    row.transient = this.transient;
    // We don't want subsequent clicks to deselect rows for single select lists
    row.togglable = this.multiselect;
  }
  /**
   * Select item that corresponds to passed value, or clear all values if value is empty string.
   */
  setSelectionsFromValue() {
    var _a;
    if (this.value || this.value === '') {
      const values = this.getCurrentSelectionValues();
      (_a = this.items) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
        if (item === this.controlRow) {
          // control row selection happens in syncControlRowWithSelections
          return;
        }
        if (!isValueEmpty(this.value) && values.has(item.value)) {
          item.silentlySelect();
          this.selectItem(item);
        }
        else {
          // value is '', so deselect all items
          item.silentlyDeselect();
          this.deselectItem(item);
        }
      });
    }
  }
  /**
   * Find any list items with the `selected` property and add to `selections`.
   */
  setSelectionsFromRowAttributes() {
    const initialSelections = new Set();
    this.items.forEach((item) => {
      if (item.selected) {
        /* TODO: Maybe figure out how to handle the case where the
         * list is not multiselect, but more than one market-row has the
         * [selected] attribute */
        initialSelections.add(item);
      }
    });
    this.selections = initialSelections;
  }
  /**
   * Sets the initial state of the list by updating and propagating props and setting
   * default value.
   */
  setInternalState() {
    this.items = [
      ...this.el.querySelectorAll(`:scope > ${getNamespacedTagFor('market-row')}, :scope > ${getNamespacedTagFor('market-action-card')}`),
    ];
    this.syncListInteractiveWithItems();
    this.processItems();
    if (this.items.length > 0) {
      if (this.value) {
        this.setSelectionsFromValue();
      }
      else {
        this.setSelectionsFromRowAttributes();
      }
    }
  }
  /**
   * Syncs the state of the slotted control row with list selections (e.g. all selected, none
   * selected, some selected).
   */
  async syncControlRowWithSelections() {
    var _a, _b, _c, _d, _e, _f;
    if (!this.controlRow) {
      return;
    }
    const slottedControl = this.controlRow.querySelector('[slot="control"]');
    const isCheckbox = slottedControl.tagName.toLowerCase() === getNamespacedTagFor('market-checkbox');
    const selectableItems = (_c = (_b = (_a = this.filteredItems) === null || _a === void 0 ? void 0 : _a.visible) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : this.items.length - 1; // -1 excludes control row
    const visibleSelectedItems = (_f = (_e = (_d = this.filteredItems) === null || _d === void 0 ? void 0 : _d.visibleSelected) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : this.selections.size;
    switch (visibleSelectedItems) {
      case 0:
        // no options selected, deselect control row
        this.deselectItem(this.controlRow);
        await this.controlRow.silentlyDeselect();
        await asyncRequestAnimationFrame(); // prevents flash where it goes from indeterminate -> checked -> unchecked
        slottedControl.removeAttribute('indeterminate');
        break;
      case selectableItems:
        // all options selected, select control row
        await this.controlRow.silentlySelect();
        this.selectItem(this.controlRow);
        slottedControl.removeAttribute('indeterminate');
        break;
      default:
        // some options selected
        if (isCheckbox) {
          // control row gets selected, checkbox set to indeterminate
          await this.controlRow.silentlySelect();
          this.selectItem(this.controlRow);
          await asyncRequestAnimationFrame(); // prevents bug where checkbox becomes checked but not indeterminate
          slottedControl.setAttribute('indeterminate', '');
        }
        else {
          // control row gets deselected
          await this.controlRow.silentlyDeselect();
          this.deselectItem(this.controlRow);
        }
        break;
    }
    this.injectCountOnControlRow();
  }
  /**
   * Injects an accessory to the control row that displays the number of items;
   * or edit that accessory's text content if the element already exists
   */
  injectCountOnControlRow() {
    if (!this.controlRow) {
      return;
    }
    const countAccessoryEl = this.controlRow.querySelector('.count[slot="trailing-accessory"]');
    const count = this.filteredItems.visible.length;
    if (countAccessoryEl) {
      countAccessoryEl.textContent = `${count}`;
    }
    else {
      const newEl = document.createElement('span');
      newEl.classList.add('count');
      newEl.setAttribute('slot', 'trailing-accessory');
      newEl.textContent = `${count}`;
      this.controlRow.appendChild(newEl);
    }
  }
  /**
   * Filters items based on search query inputted in slotted `market-input-search`
   */
  filterItems(query) {
    var _a;
    const filteredItems = this.items.reduce((filteredItems, item) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _j;
      if (item.getAttribute('slot') === 'control-row') {
        // filteredItems will not contain the control row as it isn't needed for logic purposes
        return filteredItems;
      }
      else if (!query) {
        // if there's no search query, all items are visible
        filteredItems.visible.push(item);
        return filteredItems;
      }
      else if (typeof this.filterStrategy === 'function') {
        // attempts to call the provided function
        const callbackResult = this.filterStrategy({
          item,
          label: (_a = item.querySelector('[slot="label"]')) === null || _a === void 0 ? void 0 : _a.textContent,
          query,
          textContent: item.textContent,
          value: item.value,
        });
        if (callbackResult) {
          filteredItems.visible.push(item);
          return filteredItems;
        }
      }
      else if (((_c = (_b = this.filterStrategy) === null || _b === void 0 ? void 0 : _b.toLocaleLowerCase) === null || _c === void 0 ? void 0 : _c.call(_b)) === 'textcontent') {
        // search through the entire item's textContent
        const isFound = ((_d = item === null || item === void 0 ? void 0 : item.textContent) === null || _d === void 0 ? void 0 : _d.search(new RegExp(query, 'i'))) >= 0;
        if (isFound) {
          filteredItems.visible.push(item);
          return filteredItems;
        }
      }
      else if (((_f = (_e = this.filterStrategy) === null || _e === void 0 ? void 0 : _e.toLocaleLowerCase) === null || _f === void 0 ? void 0 : _f.call(_e)) === 'label') {
        // only works if there's a slotted label
        const labelEl = item.querySelector('[slot="label"]');
        const isFound = ((_g = labelEl === null || labelEl === void 0 ? void 0 : labelEl.textContent) === null || _g === void 0 ? void 0 : _g.search(new RegExp(query, 'i'))) >= 0;
        if (isFound) {
          filteredItems.visible.push(item);
          return filteredItems;
        }
      }
      else if (((_j = (_h = this.filterStrategy) === null || _h === void 0 ? void 0 : _h.toLocaleLowerCase) === null || _j === void 0 ? void 0 : _j.call(_h)) === 'value') {
        // if the item's value isn't a string, it may not work well UX-wise
        const value = String(item.value);
        const isFound = value.search(new RegExp(query)) >= 0;
        if (isFound) {
          filteredItems.visible.push(item);
          return filteredItems;
        }
      }
      // item didn't pass any of the conditions / filter strategies above
      filteredItems.hidden.push(item);
      return filteredItems;
    }, {
      visible: [],
      hidden: [],
      visibleSelected: [],
      selected: [],
    });
    // DOM manipulation
    requestAnimationFrame(() => {
      var _a;
      // make sure that visible items are visible, hidden items are hidden
      filteredItems.visible.forEach((item) => item.classList.remove('hidden'));
      filteredItems.hidden.forEach((item) => item.classList.add('hidden'));
      // hide last visible item's bottom border
      this.items.forEach((item) => item.classList.remove('hide-bottom-border'));
      if (filteredItems.visible.length > 0) {
        const lastVisibleItem = filteredItems.visible[filteredItems.visible.length - 1];
        lastVisibleItem.classList.add('hide-bottom-border');
      }
      // hide control row if there are no search results
      (_a = this.controlRow) === null || _a === void 0 ? void 0 : _a.classList.toggle('hidden', filteredItems.visible.length === 0);
    });
    // will not emit when the list is initially rendered without a search query
    if (this.filteredItems || (!this.filteredItems && query)) {
      this.marketListItemsFiltered.emit({
        items: filteredItems.visible,
        prevItems: (_a = this.filteredItems) === null || _a === void 0 ? void 0 : _a.visible,
      });
    }
    // this triggers a re-render since `this.filteredItems` is a `@State`
    this.filteredItems = filteredItems;
    this.updateSelectedItemsInFilteredItems();
  }
  updateSelectedItemsInFilteredItems() {
    if (!this.filteredItems) {
      return;
    }
    this.filteredItems.selected = this.items.filter((item) => item.getAttribute('slot') !== 'control-row' && item.selected);
    this.filteredItems.visibleSelected = this.filteredItems.visible.filter((item) => item.getAttribute('slot') !== 'control-row' && item.selected);
  }
  /* SLOTCHANGE HANDLERS */
  handleSearchSlotchange() {
    var _a;
    this.inputSearchEl = this.el.querySelector('[slot="search"]');
    this.hasSearch = Boolean(this.inputSearchEl);
    this.filterItems((_a = this.inputSearchEl) === null || _a === void 0 ? void 0 : _a.value);
  }
  defaultSlotchangeHandler() {
    var _a;
    this.setInternalState();
    this.filterItems((_a = this.inputSearchEl) === null || _a === void 0 ? void 0 : _a.value);
    this.marketListSlotChange.emit();
  }
  /**
   * Rows slotted into the "control-row" slot only function as such if the list is interactive and
   * multiselect and the row contains a valid slotted control (checkbox or toggle).
   */
  controlRowSlotchangeHandler() {
    if (!this.interactive || !this.multiselect) {
      return;
    }
    const slottedRow = this.el.querySelector('[slot="control-row"]');
    const slottedControl = slottedRow === null || slottedRow === void 0 ? void 0 : slottedRow.querySelector('[slot="control"]');
    this.controlRow = isValidControl(slottedControl) ? slottedRow : undefined;
  }
  /**
   * Show empty state if:
   * - list isn't empty, but
   * - there is a search query, and
   * - there are no search results
   */
  setEmptyStateVisibility() {
    var _a, _b;
    const emptyStateEl = this.el.querySelector('[slot="empty-state"]') ||
      this.el.shadowRoot.querySelector(getNamespacedTagFor('market-empty-state'));
    const willShowEmptyState = ((_a = this.inputSearchEl) === null || _a === void 0 ? void 0 : _a.value) && !((_b = this.filteredItems) === null || _b === void 0 ? void 0 : _b.visible.length);
    emptyStateEl === null || emptyStateEl === void 0 ? void 0 : emptyStateEl.classList.toggle('hidden', !willShowEmptyState);
  }
  /* LIFECYCLE EVENTS */
  componentWillLoad() {
    var _a;
    this.setInternalState();
    this.filterItems((_a = this.inputSearchEl) === null || _a === void 0 ? void 0 : _a.value);
  }
  componentWillRender() {
    this.syncListInteractiveWithItems();
    this.processItems();
    this.updateSelectedItemsInFilteredItems();
    this.controlRowSlotchangeHandler();
    this.syncControlRowWithSelections();
    this.setEmptyStateVisibility();
    this.setReorderable();
  }
  /*
    KEYBOARD ACCESSIBILITY

    tabbing goes through rows (and slotted controls, if any) once before moving
    on to rest of page content

    once list has focus, up/down arrows can move focus up/down, stopping at end
    of list rather than cycling through (similar to native html <select>)
    - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select

    if slotted row contents have focus, up/down arrows do nothing
  */
  handleKeydown(e) {
    switch (e.key) {
      case 'ArrowDown':
        this.handleArrowDown(e);
        break;
      case 'ArrowUp':
        this.handleArrowUp(e);
        break;
    }
  }
  handleArrowDown(e) {
    const prevFocusedRowIndex = [...this.items].findIndex((item) => item.classList.contains('focus-visible'));
    // return if no rows are focused
    if (prevFocusedRowIndex === -1) {
      return;
    }
    // focus next enabled row
    for (let i = prevFocusedRowIndex + 1; i < this.items.length; i++) {
      if (!this.items[i].disabled) {
        this.items[i].focus();
        break;
      }
    }
    e.preventDefault(); // down arrow should not scroll page
  }
  handleArrowUp(e) {
    const prevFocusedRowIndex = [...this.items].findIndex((item) => item.classList.contains('focus-visible'));
    // return if no rows are focused
    if (prevFocusedRowIndex === -1) {
      return;
    }
    // focus last enabled row
    for (let i = prevFocusedRowIndex - 1; i >= 0; i--) {
      if (!this.items[i].disabled) {
        this.items[i].focus();
        break;
      }
    }
    e.preventDefault(); // up arrow should not scroll page
  }
  setReorderable() {
    var _a;
    (_a = this.items) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
      // control row is not reorderable
      if (item === this.controlRow)
        return;
      // only market-row is reorderable (not action cards)
      if (item.tagName !== getNamespacedTagFor('market-row').toUpperCase())
        return;
      item.dragEnabled = this.reorderable !== 'off';
    });
  }
  render() {
    var _a;
    const MarketEmptyState = getNamespacedTagFor('market-empty-state');
    return (h(Host, { class: "market-list", role: this.interactive ? 'listbox' : 'list', "aria-labelledby": this.name, "aria-multiselectable": this.multiselect, "has-search": this.hasSearch, onKeydown: (e) => this.handleKeydown(e) }, h("slot", { name: "search", onSlotchange: () => this.handleSearchSlotchange() }), h("slot", { name: "control-row", onSlotchange: () => this.controlRowSlotchangeHandler() }), h("slot", { onSlotchange: () => this.defaultSlotchangeHandler() }), h("slot", { name: "empty-state" }, h(MarketEmptyState, { class: "hidden" }, h("svg", { height: "40", slot: "media", viewBox: "0 0 40 40", width: "40", xmlns: "http://www.w3.org/2000/svg" }, h("path", { d: "M34.4667 17.2L28.1 10.8333H26.6667C26.6667 9.45 25.55 8.33333 24.1667 8.33333C22.7834 8.33333 21.6667 9.45 21.6667 10.8333H18.3334C18.3334 9.45 17.2167 8.33333 15.8334 8.33333C14.45 8.33333 13.3334 9.45 13.3334 10.8333H11.9L5.53337 17.2C4.11671 18.6167 3.33337 20.5 3.33337 22.5C3.33337 26.6333 6.70004 30 10.8334 30C14.8 30 18.0167 26.9 18.2834 23.0167C18.8167 23.2167 19.4 23.3333 20 23.3333C20.6 23.3333 21.1834 23.2167 21.7167 23.0167C21.9834 26.9 25.2 30 29.1667 30C33.3 30 36.6667 26.6333 36.6667 22.5C36.6667 20.5 35.8834 18.6167 34.4667 17.2ZM10.8334 26.6667C8.53337 26.6667 6.66671 24.8 6.66671 22.5C6.66671 21.3833 7.10004 20.3333 7.88337 19.55C8.66671 18.7667 9.71671 18.3333 10.8334 18.3333C13.1334 18.3333 15 20.2 15 22.5C15 24.8 13.1334 26.6667 10.8334 26.6667ZM15.35 16.55C14.4667 15.8833 13.4334 15.3833 12.3 15.15L13.2667 14.1667H17.2334C16.3834 14.7333 15.7167 15.5667 15.35 16.55ZM20 20C19.0834 20 18.3334 19.25 18.3334 18.3333C18.3334 17.4167 19.0834 16.6667 20 16.6667C20.9167 16.6667 21.6667 17.4167 21.6667 18.3333C21.6667 19.25 20.9167 20 20 20ZM22.75 14.1667H26.7167L27.7 15.15C26.5667 15.3833 25.5334 15.8833 24.65 16.55C24.2834 15.5667 23.6167 14.7333 22.75 14.1667ZM29.1667 26.6667C26.8667 26.6667 25 24.8 25 22.5C25 20.2 26.8667 18.3333 29.1667 18.3333C30.2834 18.3333 31.3334 18.7667 32.1167 19.55C32.9 20.3333 33.3334 21.3833 33.3334 22.5C33.3334 24.8 31.4667 26.6667 29.1667 26.6667Z", fill: "var(--core-text-10-color)", "fill-opacity": "0.9" })), h("h3", { slot: "primary-text" }, h("slot", { name: "empty-state-primary-text" }, "No search results for \u201C", (_a = this.inputSearchEl) === null || _a === void 0 ? void 0 :
      _a.value, "\u201D")), h("p", { slot: "secondary-text" }, h("slot", { name: "empty-state-secondary-text" }, "Try a different search."))))));
  }
  get el() { return getElement(this); }
  static get watchers() { return {
    "reorderable": ["reorderableWatcher"],
    "value": ["valueWatcher"]
  }; }
};
MarketList.style = marketListCss;

export { MarketList as market_list };
